// Pick to tote — a Pick Face module for internal orders (special pick
// queue 4). Lists the orders assigned to the queue on the admin Orders
// page under the same High/Low tier toggle as the Pick Queue selector
// (shared localStorage key, so a picker's tier follows them between the
// two). Tapping Pick claims that order into a one-order Large-style wave
// picked into a single tote; on completion the tote goes to Put Away
// instead of dispatch, so the picker lands back on this list.
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownToLine, ArrowLeft, ArrowUpToLine } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useTranslation } from "../i18n";
import PickToToteOrderList from "../components/picking/PickToToteOrderList";
import "../styles/picking.css";

const TIER_STORAGE_KEY = "picking.tier";

type Tier = "low" | "high";

const readStoredTier = (): Tier => {
    try {
        const v = window.localStorage.getItem(TIER_STORAGE_KEY);
        if (v === "high" || v === "low") return v;
    } catch { /* ignore */ }
    return "low";
};

const PickToTote = () => {
    usePageTitle("pick_to_tote");
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [tier, setTier] = useState<Tier>(readStoredTier);
    const [busy, setBusy] = useState(false);

    const persistTier = (next: Tier) => {
        setTier(next);
        try { window.localStorage.setItem(TIER_STORAGE_KEY, next); } catch { /* ignore */ }
    };

    const handleWaveStarted = useCallback(() => {
        // The wave is live — hand over to the Picking route, which mounts
        // ActiveWaveScreen off /picking/active.
        navigate("/picking", { replace: true });
    }, [navigate]);

    return (
        <div className="picking-selector picking-queue-orders">
            <div className="page-title-bar picking-selector-header">
                <h1 className="page-title">{t("pick_to_tote")}</h1>
                <div className="picking-tier-toggle" role="radiogroup" aria-label={t("picking_tier")}>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={tier === "low"}
                        className={`picking-tier-pill${tier === "low" ? " picking-tier-pill--active" : ""}`}
                        onClick={() => persistTier("low")}
                        disabled={busy}
                        title={t("picking_tier_low")}
                        aria-label={t("picking_tier_low")}
                    >
                        <ArrowDownToLine size={16} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={tier === "high"}
                        className={`picking-tier-pill${tier === "high" ? " picking-tier-pill--active" : ""}`}
                        onClick={() => persistTier("high")}
                        disabled={busy}
                        title={t("picking_tier_high")}
                        aria-label={t("picking_tier_high")}
                    >
                        <ArrowUpToLine size={16} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </div>
                <button
                    type="button"
                    className="page-back-btn"
                    onClick={() => navigate("/pick-face")}
                    disabled={busy}
                    aria-label={t("picking_queue_back")}
                >
                    <ArrowLeft size={20} />
                </button>
            </div>
            <PickToToteOrderList
                tier={tier}
                onWaveStarted={handleWaveStarted}
                onBusyChange={setBusy}
            />
        </div>
    );
};

export default PickToTote;
